import styled from '@emotion/styled';
import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import DeleteIcon from '@mui/icons-material/Delete';
import { Colors } from '../../styles/theme';
import { toast } from 'react-toastify';
import { AttachmentService } from '../../service/attachmentService';
import CircularProgressWithLabel from './circularProgressWithLabel';
import Grid2 from '@mui/material/Unstable_Grid2';
import useDialogModal from '../../hooks/useDialogModal';
import ConfirmationDialog from '../ui/confirmationDialog';
import validLookup from '../../utils/validLookup';
import ImageModal from './ImageModal'; // Import the ImageModal component

const Thumb = styled('img')(() => ({
    width: '100px',
    height: '100px',
    padding: '4px',
    border: '2px dashed #ccc',
    objectFit: 'cover',
    borderRadius: '5px',
    marginRight: '10px',
    cursor: 'pointer', // Add cursor pointer to indicate clickable
}));

const DeleteButton = ({ onDelete }) => {
    return (
        <IconButton
            type="button"
            onClick={onDelete}
            color='danger'
            size='large'
            sx={{
                position: 'absolute',
                right: 8,
                top: -4,
                padding: '4px',
                background: Colors.white,
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)'
            }}
        >
            <DeleteIcon />
        </IconButton>
    )
}
/**
 * 
 * Uploads a file to the server and returns the filename
 */
const AttachmentUploader = ({ onUpload, ...props }) => {
    /**
     * when a file is uploaded it goes through states and that is captured here
     */
    const [uploadFiles, setUploadFiles] = useState([])

    // error_code: description
    const [error, setError] = useState([])

    // keep track of the latest uploadFiles value.
    const uploadFilesRef = useRef(uploadFiles)
    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);
    const [selectedImage, setSelectedImage] = useState(null); // State to manage selected image for modal
    const [isModalOpen, setIsModalOpen] = useState(false); // State to manage modal open/close
    /**
     * update file upload progress 
     */
    const updateProgress = (file, progress) => {
        setUploadFiles((prevImageStates) =>
            structuredClone(prevImageStates).map((imageState) =>
                imageState.filename === file.filename ? { ...imageState, progress: progress } : imageState
            )
        );
    };

    const onDrop = useCallback(async (selectedFiles) => {
        const totalFiles = uploadFilesRef.current.length + selectedFiles.length;

        try {
            if (totalFiles <= props.maxFiles &&
                selectedFiles.length > 0 && selectedFiles.length <= props.maxFiles) {

                const Promises = []
                // create an initial state for each file dropped by the user
                const addedFiles = selectedFiles.map((file) => ({
                    file: file,
                    filename: `${Math.random().toString(36).slice(2, 10)}_${Date.now()}.${file.name.split('.').pop()}`,
                    newname: '',
                    originalname: '',
                    downloadurl: '',
                    progress: 0,
                    state: 'pending'
                }))

                setUploadFiles(prevUploads => [...prevUploads, ...addedFiles])

                // go through files, create a promise for each file and add to promises array
                addedFiles.map(async file => {
                    Promises.push(
                        new Promise(async resolve => {

                            const formData = new FormData()
                            formData.append('files', file.file)
                            //selectedFiles.map(file => formData.append('files', file))

                            // wait for upload
                            const uploadResult = await AttachmentService.upload(formData,
                                (progressUpdate) => {
                                    const progress = (progressUpdate.loaded / progressUpdate.total) * 100;
                                    updateProgress(file, progress)
                                })

                            const { code, data } = uploadResult;
                            const { newname, downloadurl, originalname } = data.files

                            if (code === 0) {
                                resolve({
                                    file: file,
                                    filename: file.filename,
                                    originalname: originalname,
                                    newname: newname,
                                    downloadurl: downloadurl,
                                    state: 'complete',
                                    progress: 100
                                })
                            } else {
                                toast.info(`Failed to upload file: ${originalname}`)
                            }

                        })
                    )
                })

                // wait for all promises to finish
                const result = await Promise.all(Promises)
                const filenames = []

                result.map(f => {
                    filenames.push({
                        originalname: f.originalname,
                        newname: f.newname,
                        downloadurl: f.downloadurl
                    })
                    setUploadFiles(prevUploadFiles => {
                        const newFileStates = structuredClone(prevUploadFiles)
                        const uploadState = newFileStates.find(file => file.filename === f.filename)

                        if (uploadState) {
                            uploadState.newname = f.newname
                            uploadState.downloadurl = f.downloadurl
                            uploadState.state = 'complete'
                        }
                        return newFileStates
                    })
                })

                //console.log(filenames)
                onUpload(filenames)
            }

        } catch (error) {
            console.log(error)
            toast.info('Failed to upload file')
        }

    }, []);

    const { getRootProps, getInputProps, fileRejections, isDragActive } = useDropzone({
        onDrop: onDrop,
        ...props
    });

    const updateError = (err) => setError((prevErrors) => {
        if (prevErrors) {
            const existingError = prevErrors.find((e) => e.code === err.code);
            if (existingError) {
                return prevErrors;
            }

            return [...prevErrors, err];
        } else {
            return [err]
        }
    })

    /**
     * to load existing attachments
     */
    useEffect(() => {
        // we need to show existing uploads
        if (props.attachments && props.attachments.length > 0) {
            const existingAttachments = props.attachments.map((attachment) => ({
                id: attachment.id,
                file: null,
                filename: `${Math.random().toString(36).slice(2, 10)}_${Date.now()}.${attachment.fileName.split('.').pop()}`,
                newname: '',
                downloadurl: attachment.path,
                progress: 100,
                state: 'complete'
            }))

            setUploadFiles(prevUploads => [...prevUploads, ...existingAttachments])
        }
    }, [props.attachments])

    useEffect(() => {
        if (!props.values) setUploadFiles([])
    }, [props.values])

    useEffect(() => {
        uploadFilesRef.current = uploadFiles

        if (uploadFilesRef.current.length === 0) {
            setError(null)
        }
    }, [uploadFiles])

    useEffect(() => {
        if (fileRejections.length > 0) {
            fileRejections.map(({ file, errors }) => errors.map(updateError));
        } else if (uploadFiles.length >= props.maxFiles) {
            updateError({ code: 'too_many_files', message: "Too many files" })
        } else {
            setError([])
        }

    }, [fileRejections])

    const _delete = (attachment) => {
        const newState = uploadFiles.filter(state => state.filename !== attachment.filename)
        setUploadFiles(newState)
        uploadFilesRef.current = newState
        setError(null)
    }
    const handleDelete = (attachment) => {
        // all uploads go into attachment table so on delete we can 
        // make a call to delete the attachment
        // if id is present than it means record already exist in db
        if (attachment.id) {
            showConfirmationDialog(async (action) => {
                if (action === validLookup.confirmationDialogAction.ok) {
                    const deleteRecordResult = await AttachmentService.deleteAttachment(attachment.id);
                    const { code, message } = deleteRecordResult;

                    if (code === 0) {
                        toast.success(message);
                        _delete(attachment)
                        if (props.onDelete) props.onDelete(attachment)
                    } else {
                        toast.success("Failed to delete file");
                    }
                }
                closeConfirmationDialog();
            }, {
                title: 'Delete attachment',
                message: `Please confirm if you would like to delete the file?`,
            });
        } else {
            _delete(attachment)
        }
    }

    const handleImageClick = (imageUrl) => {
        setSelectedImage(imageUrl);
        setIsModalOpen(true);
    };

    return (
        <Box>
            <Box {...getRootProps({ className: 'dropzone' })} sx={{
                border: '1px dashed #ccc',
                padding: '10px',
                background: Colors.body_bg,
                borderRadius: '5px',
            }}>
                <input {...getInputProps()} />
                {
                    isDragActive ? <p>Drop the file here ...</p> : <Typography variant='subtitle1'>Drag 'n' drop or click to upload file</Typography>
                }
            </Box>
            <Grid container padding={1} gap={2}>
                {
                    uploadFiles.map((file, index) => (

                        <Grid item key={index} sx={{
                            border: 0,
                            padding: 0,
                            position: 'relative',
                            height: '100%',
                        }}>
                            {
                                file.state === 'complete' ?
                                    <Thumb src={file.downloadurl} alt="Uploaded Image" onClick={() => handleImageClick(file.downloadurl)} />
                                    :
                                    <Box sx={{
                                        position: 'relative'
                                    }}>
                                        <Thumb src={URL.createObjectURL(file.file)} alt="Uploaded Image" />
                                        <Box sx={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            background: 'rgba(0,0,0,0.5)',
                                        }}>
                                            <CircularProgressWithLabel value={file.progress} />
                                        </Box>
                                    </Box>
                            }
                            <DeleteButton onDelete={() => handleDelete(file)} />
                        </Grid>
                    ))
                }
            </Grid>

            {/* error */}
            {error && error.map(e => <Typography key={e.code} sx={{ color: 'red' }}>{e.message}</Typography>)}
            {error && error.length > 0 && (<Button onClick={() => setError([])}>Clear All Errors</Button>
            )}

<ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />

            <ImageModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                imageUrl={selectedImage}
            />
        </Box>
    );
};

export default AttachmentUploader;